import { Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, of } from 'rxjs';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';

import Constants from '../constants/constants';
import { ISite } from '../interface';
import { ConfirmModalComponent } from 'src/app/shared/dialogue/confirm-modal/confirm-modal.component';

import { SessionService } from './session.service';
import { RestAPIsService } from './rest-apis.service';
import { UtilService } from './util.service';
import { AddLoaComponent } from 'src/app/shared/dialogue/add-loa/add-loa.component';
import { ProjectDateComponent } from 'src/app/shared/dialogue/project-date/project-date.component';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private sessionService: SessionService,
    private modalService: NgbModal,
    private spinner: NgxSpinnerService,
    private restAPI: RestAPIsService,
    private util: UtilService,
    private toaster: ToastrService
  ) { }

  /**
   * Show confirm popup box for info, success, error and warning
   * @param {string} header
   * @param {string} body
   */
  showMessage(header: string, body: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.modalDataConfig = {
      header,
      body,
    };
  }

  /**
   * Fetch user details from server and add/update in session storage
   * @param {string} email
   */
  getUserDetails(email: string): void {
    const payload = {
      email,
    };

    this.restAPI
      .postAPI('carbon/v1/user-details?forceRefresh=true', payload, 'domestic')
      .subscribe({
        next: (data) => {
          // Store into session
          this.sessionService.setItemIntoSession(
            Constants.sessionConstants.USERDETAILS,
            data.data[0]
          );
        },
        error: (err) => console.log('ERROR: ', err),
      });
  }

  /**
   * Check user email verification status
   * @returns {boolean}
   */
  checkEmailVerificationStatus(): boolean {
    const isVerifyEmail = this.sessionService.getItemFromSession(
      Constants.sessionConstants.ISVERIFYEMAIL
    );
    const userDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );

    return !isVerifyEmail && userDetails.firstname !== 'Guest';
  }

  /**
   * Check LOA is not signed
   * @returns {boolean}
   */
  checkLOAStatus(): boolean {
    const userDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );
    const isVerifyEmail = this.sessionService.getItemFromSession(
      Constants.sessionConstants.ISVERIFYEMAIL
    );
    return isVerifyEmail && userDetails?.loaStatus != 'SIGNED';
  }

  /**
   * Check LOA status is SIGNED
   * @returns {boolean}
   */
  get isLOASigned(): boolean {
    const siteDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTED_SITE_DATA
    );
    return siteDetails?.loaStatus == 'SIGNED';
  }

  /**
   * Check LOA is available
   * @returns {boolean}
   */
  get isLOAExist(): boolean {
    const siteDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTED_SITE_DATA
    );
    return !!siteDetails?.loaId;
  }

  /**
   * Check LOA status is AWAITING
   * @returns {boolean}
   */
  get isLOAAwaiting(): boolean {
    const siteDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTED_SITE_DATA
    );
    return siteDetails?.loaStatus == 'AWAITING';
  }

  /**
   * Check if meter type if half hourly for the selected site
   * @returns {boolean}
   */
  get isSelectedSiteMeterHH(): boolean {
    const userDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );
    const selectedSite = this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTEDSITE
    );
    const meter = userDetails.sites?.find((el: any) => el.id == selectedSite);

    return (
      meter?.profileClass == '00' || meter?.meterType?.toLowerCase() == 'h'
    );
  }

  /**
   * Check if email exist
   * @returns {boolean}
   */
  get isEmailExist(): boolean {
    const email = this.sessionService.getItemFromSession(
      Constants.sessionConstants.EMAIL
    );
    return email ? true : false;
  }

  /**
   * Check email verification if it is not Guest
   * @returns {boolean}
   */
  get isEmailVerified(): boolean {
    const isVerifyEmail = this.sessionService.getItemFromSession(
      Constants.sessionConstants.ISVERIFYEMAIL
    );
    const userDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );

    return isVerifyEmail && userDetails.firstname !== 'Guest';
  }

  /**
   * Check email verification and LOA status if meter type is half hourly for the selected site
   * @returns {boolean}
   */
  get isLoaValid(): boolean {
    const config = this.sessionService.getItemFromSession(
      Constants.CONFIG.INFO
    );
    if (this.isLOASigned) {
      return true;
    } 
    else if (config.source == 'edf') {
      this.loaConfirmationPopup();
      return false;
    }
    else {
      this.modalService.open(AddLoaComponent)
      return false;
    }

  }

  loaConfirmationPopup(): void {
    const modal = this.modalService.open(ConfirmModalComponent);
      modal.componentInstance.modalDataConfig = {
        header: 'LoA confirmation',
        body: 'Please confirm that you have verbal consent from the customer to fetch data for all the meters on the site.',
        actionBtnTitle: 'Confirm',
        closeButtonTitle: 'Cancel',
      }
      modal.componentInstance.onActionClick.subscribe((resp: any) => {
        if(resp){
          this.createLoa(modal);
        }
      })
  }

  createLoa(modal: NgbModalRef): void {
    const companySiteData = this.sessionService.getItemFromSession(Constants.sessionConstants.COMPANY_SITE_DATA);  
    const payload = {
      companyNumber: companySiteData.companyNumber,
      contactEmail: companySiteData.contactEmail?.toLocaleLowerCase(),
      customerEmail: companySiteData.customerEmail?.toLocaleLowerCase(),
      siteId: companySiteData.siteId,
    }
    this.spinner.show();
    this.restAPI.postAPI(`carbon/v3/loa`, payload, 'domestic').subscribe({
      next: (resp) =>{
        if(resp.code == 201 || resp.code == 200){
          this.toaster.success(resp.data.message)
          this.getSiteDetails(companySiteData.siteId).subscribe({
            next: (siteData) => {
              this.spinner.hide();
              modal.close();
            },
            error: (err) => {
              this.spinner.hide();
            }
          })
        }
        else{
          this.toaster.error(resp.data.message || 'Something went wrong, please try again later');
          this.spinner.hide();
        }
      },
      error: (error) => {
        if(error.error?.data){
          this.toaster.error(error.error.data)
        }
        else{
          this.toaster.error(error.error?.error?.[0]?.message || 'Something went wrong, please try again later')
        }
        console.log('err', error);
        this.spinner.hide();
      }
    })
  }

  /**
   * Return user type if exist
   * @returns {string | null}
   */
  get getUserType(): string | null {
    const user = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );
    if (user && user?.accountType) {
      return user.accountType;
    }
    return null;
  }

  /**
   * Check if user is broker
   * @returns {boolean}
   */
  // get isBroker(): boolean {
  //   const user = this.sessionService.getItemFromSession(
  //     Constants.sessionConstants.USERDETAILS
  //   );
  //   return user?.accountType == Constants.USER_TYPE.BROKER
  // }

  /**
   * Check if user is broker or customer with edit access
   * @returns {boolean}
   */
  // get isEditAccess(): boolean {
  //   const user = this.sessionService.getItemFromSession(
  //     Constants.sessionConstants.USERDETAILS
  //   );
  //   return user?.accountType == Constants.USER_TYPE.BROKER || (user?.accountType == Constants.USER_TYPE.CUSTOMER && user?.isEditAccess)
  // }

  /**
   * Return company name if exist
   * @returns {string | null}
   */
  get getUserCompany(): string | null {
    const user = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );
    if (user && user?.companyName) {
      return user.companyName;
    }
    return null;
  }

  get showLOAPopup(): boolean {
    return this.sessionService.getItemFromSession(
      Constants.sessionConstants.SHOWLOAPOPUP
    )
      ? true
      : false;
  }

  get getSelectedSiteData(): ISite {
    const userDetails = this.sessionService.getItemFromSession(
      Constants.sessionConstants.USERDETAILS
    );

    const selectedSiteId = this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTEDSITE
    );

    // Parse the selected site ID if necessary, e.g., if it's a number.
    const selectedSite = userDetails.sites.find(
      (site: any) => site.id === selectedSiteId
    );

    return selectedSite;
  }

  getSelectedSiteId(): string {
    return this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTEDSITE
    );
  }

  // Download LOA
  downloadLoa(loaId: string, customerEmail: string, companyNumber: string): void {
    const payload = new URLSearchParams({ customerEmail, companyNumber });
    this.spinner.show();
    this.restAPI.getAPI(`carbon/v3/loa/${loaId}?${payload.toString()}`, 'domestic').subscribe({
      next: (res) => {
        this.restAPI.addCheckpoint({event: 'DOWNLOAD_LOA'});
        this.util.saveFile(this.base64ToBlob(res.data, 'application/octet-stream'), 'Letter_of_Authority.pdf');
        this.spinner.hide();
      },
      error: (err) => {
        console.log('err', err);
        this.spinner.hide();
      },
    });
  }

  base64ToBlob(base64: string, mimeType: string) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  }

  /**
   * @description Get site details by siteId
   * @param { string } siteId 
   * @param { boolean } updateSession use this variable if not wish to save data in session, By default it save data in session 
   * @returns { Observable<any> }
   */
  getSiteDetails(siteId: string, updateSession: boolean = true): Observable<any> {
    return this.restAPI.getAPI(`carbon/v3/get-site-details?${new URLSearchParams({ siteId: siteId }).toString()}`, 'domestic').pipe(
      map(resp => {
        if (resp.code == 200 && updateSession) {
          this.sessionService.setItemIntoSession(Constants.sessionConstants.SELECTED_SITE_DATA, resp.data)
        }
        return resp
      })
    );
  }

  /**
   * Show popup to add project installation date
   * @param recommendation 
   * @param currentStatus 
   * @param recommendationStatus 
   * @returns { Observable<any> }
   */
  addProjectDate(recommendation:any, currentStatus: string, recommendationStatus: any, siteId?:string, email?:string): Observable<any>{
    if (this.isLoaValid) {
      if(currentStatus == 'INTERESTED' && ['heatpump', 'solarpanel', 'lighting', 'insulation', 'voltageOptimisation'].includes(recommendation.category)){
        const modalRef = this.modalService.open(ProjectDateComponent);
        modalRef.componentInstance.recommendation = recommendation;
        modalRef.componentInstance.currentStatus = currentStatus;
        modalRef.componentInstance.recommendationStatus = recommendationStatus;
        modalRef.componentInstance.siteId = siteId;
        modalRef.componentInstance.email = email;
        return modalRef.componentInstance.onSuccess;
      }
      else {
        return this.updateInterest(recommendation, currentStatus, recommendationStatus, siteId, email);
      }
    }
    else{
      return EMPTY;
    }
  }

  /**
   * Update recommendation interest status
   * @param recommendation 
   * @param currentStatus 
   * @param recommendationStatus 
   * @returns { Observable<any> }
   */
  updateInterest(recommendation:any, currentStatus: string, recommendationStatus: any = {}, siteId?:string, email?:string): Observable<any> {
    const companySiteData = this.sessionService.getItemFromSession(Constants.sessionConstants.COMPANY_SITE_DATA);
    recommendationStatus['email'] = email || companySiteData?.contactEmail;
    recommendationStatus[recommendation.category] = currentStatus;
    recommendationStatus['siteId'] =
    siteId || this.sessionService.getItemFromSession(
      Constants.sessionConstants.SELECTEDSITE
    );
    if(currentStatus == 'NOT_INTERESTED' && ['heatpump', 'solarpanel', 'lighting', 'insulation', 'voltageOptimisation'].includes(recommendation.category)){
      recommendationStatus[`${recommendation.category}_startDate`] = null;
      recommendationStatus[`${recommendation.category}_endDate`] = null;
    }
    this.spinner.show();
    
    return this.restAPI
      .postAPI(
        'carbon/v3/interests-update',
        recommendationStatus,
        'domestic'
      )
      .pipe(
        map(res => {
          this.spinner.hide();
          if (res.code == 200) {
            const titleWrap =
              recommendation.category === 'lighting'
                ? 'LIGHTING'
                : recommendation.category === 'insulation'
                  ? 'INSULATION'
                  : recommendation.category === 'heatpump'
                    ? 'HEAT_PUMP'
                    : recommendation.category === 'solarpanel'
                      ? 'SOLAR_PANELS'
                      : 'BOILER';
            // Add checkpoint
            this.restAPI.addCheckpoint({event: `${titleWrap}_INTERESTED_D`});
            const selectedSiteData = this.sessionService.getItemFromSession(Constants.sessionConstants.SELECTED_SITE_DATA)
            if(siteId == selectedSiteData?.siteId){
              const interest = { ...selectedSiteData?.interest, ...recommendationStatus }
              delete interest?.email;
              delete interest?.siteId;
              selectedSiteData['interest'] = interest
              this.sessionService.setItemIntoSession(Constants.sessionConstants.SELECTED_SITE_DATA, selectedSiteData)
            }
          }
          return res;
        }),
        catchError(err => {
          console.log('ERROR: ', err);
          this.spinner.hide();
          return of(null);
        })
      )
  }

  /**
   * @description Get recommendation title based on category
   * @param {string} category 
   * @returns 
   */
  getRecommendationTitle(category: string): any {
    if (category === Constants.recommendation.LIGHTING) {
      return 'Energy efficient lighting';
    } else if (category === Constants.recommendation.INSULATION) {
        return 'Insulation';
    } else if (category === Constants.recommendation.HEATPUMP) {
        return 'Heat pump';
    } else if (category === Constants.recommendation.SOLARPANEL || category === 'solar') {
        return 'Solar';
    } else if (category === Constants.recommendation.BOILER) {
        return 'Boiler';
    } else if (category === Constants.recommendation.VOLTAGEOPTIMISATION) {
        return 'Voltage optimisation';
    } else {
      return '';
    }

  }
}
